import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext.tsx';
import axios from 'axios';
import { requestNotificationPermission, isPushNotificationSupported, isIOS, isInStandaloneMode } from '../services/firebase.ts';

const Profile: React.FC = () => {
  const { user, logout } = useAuth();
  
  const [isEditing, setIsEditing] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);
  const [notifStatus, setNotifStatus] = useState<'idle' | 'enabled' | 'error'>('idle');
  const [notifError, setNotifError] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Mock statistics
  const statistics = {
    totalCleanings: 42,
    completedThisMonth: 8,
    punctualityRate: '98%',
    averageRating: 4.8,
  };
  
  const currentPasswordRef = useRef<HTMLInputElement>(null);

  // When isEditing becomes true, focus on the current password field
  useEffect(() => {
    if (isEditing && currentPasswordRef.current) {
      currentPasswordRef.current.focus();
    }
  }, [isEditing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate passwords
    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords don't match");
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }
    if (currentPassword === '') {
      setPasswordError("Please enter your current password");
      return;
    }
    // Reset password error if validation passed
    setPasswordError(null);
    try {
      // Call the UpdateProfile API
      const response = await axios.post('/profile/UpdateProfile', {
        OldPassword: currentPassword,
        NewPassword: newPassword,
        ConfirmPassword: confirmPassword
      });
      if (response.status === 200) {
        setIsEditing(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setNotification('Password updated successfully');
      }
    } catch (error: any) {
      // Handle API errors
      setPasswordError(error.response?.data?.message || 'Failed to update password. Please try again.');
    }
  };

  // Handle notification permission request
  const handleEnableNotifications = async () => {
    setNotifStatus('idle');
    setNotifError(null);
    setIsLoading(true);
    if (!isPushNotificationSupported) {
      setNotifError('Push notifications are not supported on iOS/Safari browsers.');
      setIsLoading(false);
      return;
    }
    try {
      const token = await requestNotificationPermission();
      if (token) {
        setNotifStatus('enabled');
      } else {
        setNotifError('Permission denied or unavailable.');
      }
    } catch (err: any) {
      setNotifError('Failed to enable notifications: ' + (err?.message || 'Unknown error'));
    }
    setIsLoading(false);
  };

  // Check if permission is already granted
  const isPermissionGranted = typeof Notification !== 'undefined' && Notification.permission === 'granted';

  return (
    <div className="container-app py-6">
      <h1 className="text-2xl font-bold mb-6">My Profile</h1>

      {notification && (
        <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {notification}
        </div>
      )}

      {/* Profile Card */}
      <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
        <div className="p-6 sm:p-8 md:flex">
          <div className="md:flex-shrink-0 flex justify-center">
            <div className="relative">
              <img
                className="h-32 w-32 rounded-full object-cover border-4 border-white shadow"
                src={user?.avatar || `https://ui-avatars.com/api/?name=${user?.name || 'User'}&background=dd4b39&color=fff&size=128`}
                alt={user?.name}
              />
              {user?.role === 'admin' && (
                <span className="absolute bottom-0 right-0 bg-primary text-white text-xs px-2 py-1 rounded-full">
                  Admin
                </span>
              )}
            </div>
          </div>
          <div className="mt-6 md:mt-0 md:ml-6 text-center md:text-left">
            <h2 className="text-xl font-bold">{user?.name}</h2>
            <p className="text-gray-600">{user?.email}</p>
            <div className="mt-4">
              <button
                onClick={() => setIsEditing(true)}
                className="btn btn-primary mr-2"
              >
                Change Password
              </button>
              <button 
                onClick={logout}
                className="btn bg-gray-200 text-gray-800 hover:bg-gray-300"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Status */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-bold mb-4">Notification Settings</h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-800 font-medium">Push Notifications</p>
            <p className="text-sm text-gray-600">
              Get notified about new schedules, updates, and other important information
            </p>
          </div>
          <div className="flex items-center">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mr-3 
              ${isPermissionGranted || notifStatus === 'enabled' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
              {isPermissionGranted || notifStatus === 'enabled' ? 'Enabled' : 'Disabled'}
            </span>
            {!(isPermissionGranted || notifStatus === 'enabled') && (
              <button
                onClick={handleEnableNotifications}
                disabled={isLoading || !isPushNotificationSupported}
                className={`flex items-center px-4 py-2 rounded-md text-white font-medium
                  ${isLoading || !isPushNotificationSupported ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-primary-dark'}
                  transition-colors duration-200`}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
                    </svg>
                    Enable
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        {notifStatus === 'enabled' && (
          <p className="text-green-600 text-sm mt-2">Notifications enabled!</p>
        )}
        {notifError && (
          <p className="text-red-600 text-sm mt-2">{notifError}</p>
        )}
        {!isPushNotificationSupported && (
          <p className="text-red-500 text-xs mt-2">
            Push notifications are not supported on iOS/Safari browsers.
          </p>
        )}
        {isIOS && !isInStandaloneMode() && (
          <p className="text-yellow-600 text-xs mt-2">
            To enable push notifications, install this app to your home screen and open it from there.
          </p>
        )}
      </div>

      {/* Statistics */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-bold mb-4">My Statistics</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-100 p-4 rounded-lg text-center">
            <p className="text-gray-600 text-sm">Total Cleanings</p>
            <p className="text-2xl font-bold text-primary">{statistics.totalCleanings}</p>
          </div>
          <div className="bg-gray-100 p-4 rounded-lg text-center">
            <p className="text-gray-600 text-sm">This Month</p>
            <p className="text-2xl font-bold text-blue-600">{statistics.completedThisMonth}</p>
          </div>
          <div className="bg-gray-100 p-4 rounded-lg text-center">
            <p className="text-gray-600 text-sm">Punctuality</p>
            <p className="text-2xl font-bold text-green-600">{statistics.punctualityRate}</p>
          </div>
          <div className="bg-gray-100 p-4 rounded-lg text-center">
            <p className="text-gray-600 text-sm">Avg. Rating</p>
            <p className="text-2xl font-bold text-yellow-600">{statistics.averageRating}</p>
          </div>
        </div>
      </div>

      {/* Change Password Form */}
      {isEditing && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold mb-4">Change Password</h2>
          <form onSubmit={handleSubmit}>
            {passwordError && (
              <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {passwordError}
              </div>
            )}
            <div className="mb-4">
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Current Password
              </label>
              <input
                ref={currentPasswordRef}
                type="password"
                id="currentPassword"
                className="input"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
            <div className="mb-4">
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                className="input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <div className="mb-6">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                className="input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary w-full"
            >
              Update Password
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Profile; 